import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  DynamicFormActionTypes,
  LoadDynamicFormConnections,
  LoadDynamicFormConnectionsFailure,
  LoadDynamicFormConnectionsSuccess,
  LoadDynamicFormData,
  LoadDynamicFormDataFailure,
  LoadDynamicFormDataSuccess,
  SaveDynamicForm,
  SaveDynamicFormFailure,
  SaveDynamicFormSuccess,
} from '@completion/actions';
import { ResourceType } from '@completion/enums';
import { ConnectedFormDto } from '@completion/models';
import { DynamicFormService } from '@completion/services';
import type { State } from '@completion/reducers';
import { trackResource } from './resource-fetch.util';
import { NotificationService } from '../../services/notification.service';

@Injectable()
export class DynamicFormEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<State>,
    private readonly dynamicFormService: DynamicFormService,
    private readonly toastr: NotificationService,
  ) {}

  loadDynamicFormConnections$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadDynamicFormConnections>(DynamicFormActionTypes.LoadDynamicFormConnections),
      switchMap((action) =>
        of(action).pipe(
          trackResource<ConnectedFormDto>({
            actions$: this.actions$,
            store: this.store,
            toastr: this.toastr,
            service: this.dynamicFormService,
            method: 'getDynamicForm',
            descriptor: { type: ResourceType.DynamicFormConnectionsLoad },
            serviceArgs: (a) => [a.projectId, a.csNumber],
            successAction: LoadDynamicFormConnectionsSuccess,
            mapSuccessArgs: (response) => [response.connectedFormGuids ?? []],
            errorAction: LoadDynamicFormConnectionsFailure,
            mapErrorArgs: () => ['Failed to load connected forms.'],
          }),
        ),
      ),
    ),
  );

  loadDynamicFormData$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadDynamicFormData>(DynamicFormActionTypes.LoadDynamicFormData),
      switchMap((action) => {
        if (!action.formGuids?.length) {
          return of(new LoadDynamicFormDataSuccess([], []));
        }
        return of(action).pipe(
          trackResource({
            actions$: this.actions$,
            store: this.store,
            toastr: this.toastr,
            service: this.dynamicFormService,
            method: 'getDynamicFormByGuids',
            descriptor: { type: ResourceType.DynamicFormLoad },
            serviceArgs: (a) => [a.formGuids],
            successAction: LoadDynamicFormDataSuccess,
            errorAction: LoadDynamicFormDataFailure,
            mapErrorArgs: () => ['Failed to load dynamic forms.'],
          }),
        );
      }),
    ),
  );

  saveDynamicForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SaveDynamicForm>(DynamicFormActionTypes.SaveDynamicForm),
      switchMap((action) => {
        if (!action.formData?.length) {
          return of(new SaveDynamicFormFailure('No form data to save.'));
        }
        return of(action).pipe(
          trackResource({
            actions$: this.actions$,
            store: this.store,
            toastr: this.toastr,
            service: this.dynamicFormService,
            method: 'saveDynamicFormSubmissions',
            descriptor: { type: ResourceType.DynamicFormSave },
            serviceArgs: (a) => [a.formData],
            onSuccess: () => {
              this.toastr.success('Check sheet form values saved.');
              return new SaveDynamicFormSuccess();
            },
            errorAction: SaveDynamicFormFailure,
            mapErrorArgs: () => ['Failed to save check sheet form values.'],
          }),
        );
      }),
    ),
  );
}
